import { v4 as uuidv4 } from 'uuid'
import {
  IMeetingRoom,
  IMeetingRoomEquipmentItem,
  IMeetingRoomFile,
  IMeetingRoomFormData,
  IMeetingRoomImage,
} from '../types'
import {
  MEETING_ROOM_DESCRIPTION_MAX_LENGTH,
  MEETING_ROOM_EMPTY_GUID,
  MEETING_ROOM_SLIDER_FILE_TYPE,
  MEETING_ROOM_SUPPORT_FILE_TYPE,
  MeetingRoomAudienceCode,
  MeetingRoomPurposeCode,
  parseMeetingRoomPurposeCode,
} from './constants'
import { extractListRows } from '../../shared/apiResponse'
import { buildMeetingRoomFilesForSave } from './meetingRoomFiles'
import { HubMeetingRoomSaveRequest, MeetingRoomFileDto } from './types'

const readApiField = <T>(row: Record<string, unknown>, camel: string, pascal: string): T | undefined =>
  (row[camel] ?? row[pascal]) as T | undefined

const parseApiNumber = (value: unknown, fallback: number): number => {
  const parsed = Number(value)
  return Number.isNaN(parsed) ? fallback : parsed
}

const mapEquipmentFromDto = (equipment: unknown): IMeetingRoomEquipmentItem | null => {
  if (equipment == null || typeof equipment !== 'object') return null
  const row = equipment as Record<string, unknown>
  const name = row.name ?? row.Name
  if (typeof name !== 'string' || name.trim() === '') return null
  const id = readApiField<string>(row, 'id', 'Id')
  return {
    id: id != null && String(id).trim() !== '' ? String(id) : undefined,
    name: name.trim(),
  }
}

const isPersistedEquipmentId = (id: string | undefined): boolean =>
  id != null &&
  id.trim() !== '' &&
  id !== MEETING_ROOM_EMPTY_GUID

const buildEquipmentsSavePayload = (
  current: IMeetingRoomEquipmentItem[],
  initial?: IMeetingRoomEquipmentItem[]
): HubMeetingRoomSaveRequest['equipments'] => {
  const active = current
    .map(item => ({ ...item, name: item.name.trim() }))
    .filter(item => item.name !== '')
    .map(item => ({
      id: isPersistedEquipmentId(item.id) ? String(item.id) : MEETING_ROOM_EMPTY_GUID,
      name: item.name,
    }))

  const activeIds = new Set(
    active.map(item => item.id).filter(id => id !== MEETING_ROOM_EMPTY_GUID)
  )

  const removed = (initial ?? [])
    .filter(item => isPersistedEquipmentId(item.id) && !activeIds.has(String(item.id)))
    .map(item => ({
      id: String(item.id),
      name: item.name,
      isDeleted: true,
    }))

  return [...active, ...removed]
}

const normalizeMeetingRoomFileDto = (file: unknown): MeetingRoomFileDto | null => {
  if (file == null || typeof file !== 'object') return null
  const row = file as Record<string, unknown>
  const id = readApiField<string>(row, 'id', 'Id')
  const path = readApiField<string>(row, 'path', 'Path')
  if (id == null || path == null) return null
  return {
    id: String(id),
    path,
    mimeType: readApiField<string>(row, 'mimeType', 'MimeType'),
    originalName:
      readApiField<string>(row, 'originalName', 'OriginalName') ??
      readApiField<string>(row, 'name', 'Name'),
    extension: readApiField<string>(row, 'extension', 'Extension'),
    size: readApiField<number>(row, 'size', 'Size'),
    fileType:
      readApiField<string>(row, 'fileType', 'FileType') ??
      readApiField<string>(row, 'type', 'Type'),
  }
}

const normalizeMeetingRoomFiles = (files: unknown[] | undefined): MeetingRoomFileDto[] =>
  (files ?? [])
    .map(normalizeMeetingRoomFileDto)
    .filter((file): file is MeetingRoomFileDto => file != null)

const splitMeetingRoomFilesByType = (
  files: unknown[] | undefined
): {
  coverImages: MeetingRoomFileDto[]
  sliderImages: MeetingRoomFileDto[]
  supportFiles: MeetingRoomFileDto[]
} => {
  const coverImages: MeetingRoomFileDto[] = []
  const sliderImages: MeetingRoomFileDto[] = []
  const supportFiles: MeetingRoomFileDto[] = []
  for (const raw of files ?? []) {
    const file = normalizeMeetingRoomFileDto(raw)
    if (file == null) continue
    const type = (file.fileType ?? '').toLowerCase()
    if (type === MEETING_ROOM_SUPPORT_FILE_TYPE.toLowerCase()) {
      supportFiles.push(file)
    } else if (type === MEETING_ROOM_SLIDER_FILE_TYPE.toLowerCase()) {
      sliderImages.push(file)
    } else {
      coverImages.push(file)
    }
  }
  return { coverImages, sliderImages, supportFiles }
}

const unwrapMeetingRoomPayload = (
  item: unknown
): {
  id?: string
  name: string
  description: string
  purpose: number
  audience: number
  allowFullReservation: boolean
  maximumCapacity: number
  chairQuantity: number
  isHidden: boolean
  equipments: unknown[]
  coverImages: MeetingRoomFileDto[]
  sliderImages: MeetingRoomFileDto[]
  supportFiles: MeetingRoomFileDto[]
} => {
  if (item == null || typeof item !== 'object') {
    return {
      name: '',
      description: '',
      purpose: MeetingRoomPurposeCode.Event,
      audience: MeetingRoomAudienceCode.Private,
      allowFullReservation: true,
      maximumCapacity: 0,
      chairQuantity: 0,
      isHidden: false,
      equipments: [],
      coverImages: [],
      sliderImages: [],
      supportFiles: [],
    }
  }

  const row = item as Record<string, unknown>
  const nested = row.data
  const source =
    nested != null && typeof nested === 'object' && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : row

  const id =
    readApiField<string>(source, 'id', 'Id') ??
    readApiField<string>(row, 'id', 'Id')

  const filesRaw = readApiField<unknown[]>(source, 'files', 'Files')
  const { coverImages, sliderImages, supportFiles } = splitMeetingRoomFilesByType(filesRaw)

  const legacyImages = normalizeMeetingRoomFiles(
    readApiField<unknown[]>(source, 'images', 'Images')
  )
  const mappedCoverImages =
    coverImages.length > 0
      ? coverImages
      : legacyImages.filter(
          file =>
            (file.fileType ?? '').toLowerCase() !== MEETING_ROOM_SLIDER_FILE_TYPE.toLowerCase()
        )
  const mappedSliderImages =
    sliderImages.length > 0
      ? sliderImages
      : legacyImages.filter(
          file =>
            (file.fileType ?? '').toLowerCase() === MEETING_ROOM_SLIDER_FILE_TYPE.toLowerCase()
        )

  const mappedSupport =
    supportFiles.length > 0
      ? supportFiles
      : normalizeMeetingRoomFiles(readApiField<unknown[]>(source, 'supportFiles', 'SupportFiles'))

  const equipmentsRaw = readApiField<unknown[]>(source, 'equipments', 'Equipments') ?? []

  return {
    id: id != null ? String(id) : undefined,
    name: readApiField<string>(source, 'name', 'Name') ?? '',
    description: readApiField<string>(source, 'description', 'Description') ?? '',
    purpose: parseMeetingRoomPurposeCode(readApiField<string | number>(source, 'purpose', 'Purpose')),
    audience: parseApiNumber(readApiField(source, 'audience', 'Audience'), MeetingRoomAudienceCode.Private),
    allowFullReservation:
      readApiField<boolean>(source, 'allowFullReservation', 'AllowFullReservation') ?? true,
    maximumCapacity:
      parseApiNumber(readApiField(source, 'maximumCapacity', 'MaximumCapacity'), 0),
    chairQuantity: parseApiNumber(readApiField(source, 'chairQuantity', 'ChairQuantity'), 0),
    isHidden: readApiField<boolean>(source, 'isHidden', 'IsHidden') ?? false,
    equipments: equipmentsRaw,
    coverImages: mappedCoverImages,
    sliderImages: mappedSliderImages,
    supportFiles: mappedSupport,
  }
}

export const normalizeMeetingRoomListResponse = (response: unknown): IMeetingRoom[] =>
  extractListRows(response).map(mapMeetingRoomDtoToModel)

export const normalizeMeetingRoomDetailResponse = (response: unknown): IMeetingRoom | null => {
  if (response == null) return null
  const raw = response as Record<string, unknown>
  const item =
    raw.data != null && typeof raw.data === 'object' && !Array.isArray(raw.data)
      ? { id: raw.id ?? raw.Id, data: raw.data }
      : raw
  return mapMeetingRoomDtoToModel(item)
}

const mapFileDtoToModel = (file: MeetingRoomFileDto): IMeetingRoomFile => ({
  id: file.id,
  path: file.path ?? '',
  mimeType: file.mimeType,
  originalName: file.originalName,
  extension: file.extension,
  size: file.size,
})

const mapFileToImage = (file: MeetingRoomFileDto): IMeetingRoomImage => {
  const path = file.path ?? ''
  return {
    id: file.id,
    path,
    name: file.originalName ?? path,
  }
}

export const mapMeetingRoomDtoToModel = (item: unknown): IMeetingRoom => {
  const payload = unwrapMeetingRoomPayload(item)
  const coverImages = payload.coverImages.map(mapFileToImage)
  const sliderImages = payload.sliderImages.map(mapFileToImage)

  return {
    id: String(payload.id ?? ''),
    name: payload.name,
    description: payload.description,
    maxCapacity: payload.maximumCapacity ?? 0,
    equipment: payload.equipments
      .map(mapEquipmentFromDto)
      .filter((item): item is IMeetingRoomEquipmentItem => item != null),
    coverImages,
    sliderImages,
    images: coverImages.length > 0 ? coverImages : sliderImages,
    rulesFile: payload.supportFiles[0]?.originalName,
    existingSupportFiles: payload.supportFiles.map(mapFileDtoToModel),
    status: payload.isHidden ? 'unavailable' : 'available',
    purposeCode: payload.purpose,
    audienceCode: payload.audience,
    allowFullReservation: payload.allowFullReservation,
    chairQuantity: payload.chairQuantity,
    isHidden: payload.isHidden,
  }
}

export const mapMeetingRoomToFormData = (room: IMeetingRoom): Partial<IMeetingRoomFormData> => ({
  name: room.name,
  purpose: String(room.purposeCode ?? MeetingRoomPurposeCode.Event),
  description: room.description,
  maxCapacity: room.maxCapacity,
  chairQuantity: room.chairQuantity ?? 0,
  equipment: room.equipment.length > 0 ? room.equipment : [{ name: '' }],
  initialEquipment: room.equipment.map(item => ({ ...item })),
  coverImages: [],
  sliderImages: [],
  rulesFile: null,
  existingCoverImages: room.coverImages,
  existingSliderImages: room.sliderImages,
  existingSupportFiles: room.existingSupportFiles ?? [],
  isHidden: room.isHidden ?? false,
})

const isPersistedMeetingRoomId = (id: string | undefined): boolean =>
  id != null && id.trim() !== '' && id !== MEETING_ROOM_EMPTY_GUID

/** Extrai o id da sala do corpo do POST /v1/hub/company/meeting-room ({ id, data: { id, ... } }). */
export const extractMeetingRoomSaveId = (
  response: unknown,
  fallbackId?: string
): string | undefined => {
  const axiosBody =
    response != null &&
    typeof response === 'object' &&
    'data' in response &&
    (response as { data?: unknown }).data != null &&
    typeof (response as { data?: unknown }).data === 'object' &&
    !Array.isArray((response as { data?: unknown }).data)
      ? (response as { data: unknown }).data
      : response

  if (axiosBody == null || typeof axiosBody !== 'object') {
    return isPersistedMeetingRoomId(fallbackId) ? fallbackId : undefined
  }

  const body = axiosBody as Record<string, unknown>
  const nested =
    body.data != null && typeof body.data === 'object' && !Array.isArray(body.data)
      ? (body.data as Record<string, unknown>)
      : null

  const candidates = [
    nested?.id,
    nested?.Id,
    body.id,
    body.Id,
    fallbackId,
  ]

  for (const candidate of candidates) {
    if (candidate == null) continue
    const id = String(candidate).trim()
    if (isPersistedMeetingRoomId(id)) return id
  }

  return undefined
}

export const buildMeetingRoomSavePayload = async (
  form: IMeetingRoomFormData,
  meetingRoomId?: string
): Promise<{ id: string; data: HubMeetingRoomSaveRequest }> => {
  const purpose = parseMeetingRoomPurposeCode(form.purpose)
  const isCoworking = purpose === MeetingRoomPurposeCode.Coworking
  const isUpdate = meetingRoomId != null && meetingRoomId !== ''
  const dataId = isUpdate ? meetingRoomId : MEETING_ROOM_EMPTY_GUID
  const requestId = isUpdate ? meetingRoomId : uuidv4()
  const { images, supportFiles } = await buildMeetingRoomFilesForSave(form)

  const data: HubMeetingRoomSaveRequest = {
    id: dataId,
    name: form.name.trim(),
    description: form.description
      .trim()
      .slice(0, MEETING_ROOM_DESCRIPTION_MAX_LENGTH),
    purpose,
    audience: MeetingRoomAudienceCode.Private,
    allowFullReservation: true,
    maximumCapacity: isCoworking ? null : Number(form.maxCapacity) || 0,
    chairQuantity: isCoworking ? Number(form.chairQuantity) || 0 : 0,
    isHidden: form.isHidden ?? false,
    equipments: buildEquipmentsSavePayload(form.equipment, form.initialEquipment),
    images,
    supportFiles,
    chairs: [],
  }

  return { id: requestId, data }
}
